import { useState } from 'react';
import { FileText, Download, Edit3, Plus, TrendingUp, Wrench, Undo2, GitBranch, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  useConversationArtifacts,
  useArtifactVersions,
  type ArtifactRow,
  type ArtifactVersionRow,
} from '@/hooks/useArtifactVersions';
import { callRpc } from '@/lib/supabaseTyped';
import { toast } from '@/hooks/use-toast';

export interface FlowSidebarProps {
  conversationId: string | null;
  onLoadVersion: (versionId: string, artifactId: string) => Promise<void>;
  onCompareVersions?: (v1: string, v2: string) => void;
  /**
   * I-104: "Fortsätt bygga"-CTA. Kallas med versionId → parent:en ska
   * ladda versionen till canvas, sätta intent='extend' och focus:a
   * chat-input så user kan skriva direkt. Om undefined visas ingen CTA.
   */
  onContinueBuilding?: (versionId: string, artifactId: string) => void | Promise<void>;
}

const OPERATION_ICONS = {
  build_new: Plus,
  extend: TrendingUp,
  modify_in_place: Edit3,
  refactor: Wrench,
  revert: Undo2,
} as const;

const OPERATION_COLORS = {
  build_new: 'bg-green-100 text-green-700 border-green-300',
  extend: 'bg-blue-100 text-blue-700 border-blue-300',
  modify_in_place: 'bg-amber-100 text-amber-700 border-amber-300',
  refactor: 'bg-purple-100 text-purple-700 border-purple-300',
  revert: 'bg-gray-100 text-gray-700 border-gray-300',
} as const;

export function FlowSidebar({ conversationId, onLoadVersion, onCompareVersions, onContinueBuilding }: FlowSidebarProps) {
  const { data: artifacts } = useConversationArtifacts(conversationId);
  const [expandedArtifactId, setExpandedArtifactId] = useState<string | null>(null);

  if (!conversationId) {
    return (
      <div
        className="flex flex-col h-full border-l bg-muted/20 items-center justify-center p-4 text-center"
        data-testid="flow-sidebar-empty"
      >
        <FileText className="w-8 h-8 text-muted-foreground mb-2" />
        <div className="text-xs text-muted-foreground">
          Välj en chatt för att se dess flöden
        </div>
      </div>
    );
  }

  const hasArtifacts = (artifacts?.length ?? 0) > 0;

  return (
    <div className="flex flex-col h-full border-l bg-muted/20" data-testid="flow-sidebar">
      <div className="p-2 border-b">
        <div className="text-xs font-semibold flex items-center gap-1">
          <GitBranch className="w-3.5 h-3.5" /> Flöden i denna chatt
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {!hasArtifacts && (
            <div className="text-xs text-muted-foreground p-2 text-center">
              Inga flöden genererade ännu. Börja skriva till AI:n.
            </div>
          )}
          {artifacts?.map((artifact) => (
            <ArtifactBlock
              key={artifact.id}
              artifact={artifact}
              expanded={expandedArtifactId === artifact.id}
              onToggleExpand={() =>
                setExpandedArtifactId(
                  expandedArtifactId === artifact.id ? null : artifact.id,
                )
              }
              onLoadVersion={(versionId) => onLoadVersion(versionId, artifact.id)}
              onCompareVersions={onCompareVersions}
              onContinueBuilding={
                onContinueBuilding
                  ? (versionId) => onContinueBuilding(versionId, artifact.id)
                  : undefined
              }
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

interface ArtifactBlockProps {
  artifact: ArtifactRow;
  expanded: boolean;
  onToggleExpand: () => void;
  onLoadVersion: (versionId: string) => void;
  onCompareVersions?: (v1: string, v2: string) => void;
  onContinueBuilding?: (versionId: string) => void | Promise<void>;
}

function ArtifactBlock({
  artifact, expanded, onToggleExpand, onLoadVersion, onCompareVersions, onContinueBuilding,
}: ArtifactBlockProps) {
  const { data: versions } = useArtifactVersions(expanded ? artifact.id : null);
  const [renaming, setRenaming] = useState(false);
  const [titleValue, setTitleValue] = useState(artifact.title);
  const [compareA, setCompareA] = useState<string | null>(null);

  const commitTitleRename = async () => {
    const v = titleValue.trim();
    if (v.length > 0 && v.length <= 120 && v !== artifact.title) {
      const { data, error } = await callRpc<{ success: boolean; error?: string }>(
        'rename_artifact',
        { p_artifact_id: artifact.id, p_new_title: v },
      );
      if (error || !data?.success) {
        toast({ title: 'Kunde inte byta namn', variant: 'destructive' });
      }
    }
    setRenaming(false);
  };

  return (
    <div className="border rounded-md bg-background" data-testid={`artifact-${artifact.id}`}>
      <div className="p-2 flex items-center gap-1.5">
        {renaming ? (
          <Input
            autoFocus
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitTitleRename();
              if (e.key === 'Escape') {
                setTitleValue(artifact.title);
                setRenaming(false);
              }
            }}
            onBlur={commitTitleRename}
            className="h-6 text-xs flex-1"
          />
        ) : (
          <button
            type="button"
            onClick={onToggleExpand}
            onDoubleClick={() => setRenaming(true)}
            className="flex-1 text-left text-xs font-medium truncate"
            title={artifact.title}
          >
            {artifact.title}
          </button>
        )}
        <Badge variant="outline" className="text-[9px]">
          {artifact.version_count}v
        </Badge>
      </div>

      {expanded && versions && (
        <div className="border-t px-2 py-1 space-y-1">
          {versions.map((v) => {
            const Icon = OPERATION_ICONS[v.operation] ?? FileText;
            const colorClass = OPERATION_COLORS[v.operation] ?? OPERATION_COLORS.refactor;
            const isCurrent = artifact.current_version_id === v.id;
            const regressionFlag =
              v.regression_check_result &&
              typeof v.regression_check_result === 'object' &&
              (v.regression_check_result as { clean?: boolean }).clean === false;

            return (
              <div
                key={v.id}
                className={`flex items-center gap-1.5 py-1 px-1 rounded text-xs ${
                  isCurrent ? 'bg-primary/10' : 'hover:bg-accent/50'
                }`}
              >
                <div className={`rounded-sm border px-1 text-[9px] ${colorClass} flex-shrink-0`}>
                  <Icon className="w-2.5 h-2.5 inline" /> v{v.version_number}
                </div>
                <VersionLabelEditor
                  versionId={v.id}
                  operation={v.operation}
                  label={v.label}
                />
                {regressionFlag && (
                  <Badge variant="destructive" className="text-[9px]" title="Regression">
                    !
                  </Badge>
                )}
                <div className="flex gap-0.5">
                  {onCompareVersions && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5"
                      onClick={() => {
                        if (compareA === null) {
                          setCompareA(v.id);
                          toast({ title: 'Välj version 2 att jämföra' });
                        } else if (compareA !== v.id) {
                          onCompareVersions(compareA, v.id);
                          setCompareA(null);
                        }
                      }}
                      title="Jämför med annan version"
                    >
                      <GitBranch className="w-3 h-3" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-5 w-5"
                    onClick={() => onLoadVersion(v.id)}
                    title="Ladda till canvas"
                    data-testid={`load-version-${v.id}`}
                  >
                    <Download className="w-3 h-3" />
                  </Button>
                  {onContinueBuilding && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5"
                      onClick={() => void onContinueBuilding(v.id)}
                      title="Fortsätt bygga från denna version"
                      data-testid={`continue-building-${v.id}`}
                    >
                      <PlayCircle className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface VersionLabelEditorProps {
  versionId: string;
  operation: ArtifactVersionRow['operation'];
  label: string | null;
}

function VersionLabelEditor({ versionId, operation, label }: VersionLabelEditorProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label ?? '');

  const commit = async () => {
    const v = value.trim();
    const { data, error } = await callRpc<{ success: boolean; error?: string }>(
      'rename_artifact_version',
      { p_version_id: versionId, p_label: v.length === 0 ? null : v },
    );
    if (error || !data?.success) {
      toast({ title: 'Kunde inte sätta label', variant: 'destructive' });
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        onBlur={commit}
        className="h-5 text-[10px] flex-1"
        placeholder={operation}
      />
    );
  }

  return (
    <button
      type="button"
      onDoubleClick={() => {
        setValue(label ?? '');
        setEditing(true);
      }}
      className="flex-1 text-left text-[10px] truncate"
      title="Dubbelklicka för att byta namn"
    >
      {label || <span className="text-muted-foreground italic">{operation}</span>}
    </button>
  );
}
